import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  useGrantOfflineCandidateConsent,
  useRequestCandidateConsent,
} from '@/hooks/use-candidates';
import { Mail, PenLine } from 'lucide-react';
import { useState } from 'react';
import { consentEmailLabel } from '../lib/consent-actions';
import { DisabledReasonWrap } from './candidate-gdpr-quick-actions';

interface CandidateGdprConsentRequestProps {
  candidateId: string;
  hasEmail: boolean;
  lastRequestedAt: string | null;
}

/**
 * Asking for consent: by email or recorded offline. Shown in the status card
 * while consent is Pending or Expired [HW-1055] — moved from Quick actions.
 */
export function CandidateGdprConsentRequest({
  candidateId,
  hasEmail,
  lastRequestedAt,
}: CandidateGdprConsentRequestProps) {
  const [grantOpen, setGrantOpen] = useState(false);
  const request = useRequestCandidateConsent(candidateId);

  const resendReason = !hasEmail ? 'Candidate has no email on file' : null;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-wrap gap-2">
          <DisabledReasonWrap reason={resendReason}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => request.mutate({})}
              disabled={!hasEmail || request.isPending}
            >
              <Mail className="mr-2 h-4 w-4" />
              {request.isPending ? 'Sending…' : consentEmailLabel(lastRequestedAt)}
            </Button>
          </DisabledReasonWrap>
          <Button type="button" variant="outline" size="sm" onClick={() => setGrantOpen(true)}>
            <PenLine className="mr-2 h-4 w-4" />
            Record offline consent
          </Button>
        </div>
      </TooltipProvider>

      <GrantOfflineConsentDialog
        candidateId={candidateId}
        open={grantOpen}
        onOpenChange={setGrantOpen}
      />
    </>
  );
}

function GrantOfflineConsentDialog({
  candidateId,
  open,
  onOpenChange,
}: {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [policyVersion, setPolicyVersion] = useState('v1.0');
  const [note, setNote] = useState('');
  const grant = useGrantOfflineCandidateConsent(candidateId);

  const handleSubmit = async () => {
    await grant.mutateAsync({
      policyVersion: policyVersion.trim(),
      note: note.trim() || undefined,
    });
    onOpenChange(false);
    setNote('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Record offline consent</DialogTitle>
          <DialogDescription>
            Log a manual GRANTED event (paper form, phone, in person) and flip the candidate's GDPR
            status to Approved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="policy-version">Policy version</Label>
            <Input
              id="policy-version"
              value={policyVersion}
              onChange={(e) => setPolicyVersion(e.target.value)}
              placeholder="v1.0"
              maxLength={20}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="consent-note">Note (optional)</Label>
            <Textarea
              id="consent-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Signed paper form at onsite interview on 2026-04-12"
              maxLength={1000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={grant.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={handleSubmit}
            disabled={!policyVersion.trim() || grant.isPending}
          >
            {grant.isPending ? 'Saving…' : 'Record consent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
